import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  RefreshCw, 
  CheckCircle, 
  AlertCircle,
  BarChart3,
  LineChart
} from "lucide-react";

const GoogleAccountSelectPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [error, setError] = useState(null);
  
  const token = searchParams.get("token");
  const type = searchParams.get("type"); // "google_ads" or "ga4"

  useEffect(() => {
    if (!token || !type) {
      setError("Missing token or type parameter");
      setLoading(false);
      return;
    }

    const fetchAccounts = async () => {
      try {
        if (type === "google_ads") {
          const response = await axios.get(`${API}/integrations/google-ads/accounts`, {
            params: { token },
          });
          setAccounts(response.data.accounts || []);
        } else if (type === "ga4") {
          const response = await axios.get(`${API}/integrations/ga4/properties`, {
            params: { token },
          });
          setProperties(response.data.properties || []);
        }
      } catch (error) {
        console.error("Error fetching accounts:", error);
        setError(error.response?.data?.detail || "Failed to load accounts");
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [token, type]);

  const selectAccount = async (accountId, accountName) => {
    setSelecting(true);
    try {
      if (type === "google_ads") {
        const response = await axios.post(
          `${API}/integrations/google-ads/select-account`,
          { account_id: accountId, account_name: accountName },
          { params: { token } }
        );
        toast.success("Google Ads account connected!");
        window.location.href = response.data.redirect_url;
      } else if (type === "ga4") {
        const response = await axios.post(
          `${API}/integrations/ga4/select-property`,
          { property_id: accountId, property_name: accountName },
          { params: { token } }
        );
        toast.success("GA4 property connected!");
        window.location.href = response.data.redirect_url;
      }
    } catch (error) {
      console.error("Error selecting account:", error);
      toast.error(error.response?.data?.detail || "Failed to select account");
      setSelecting(false);
    }
  };

  const goBack = () => {
    navigate("/admin?tab=integrations");
  };

  const retryConnection = () => {
    navigate("/admin?tab=integrations&retry=google_ads");
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Connection Error</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-gray-500 mb-4">
              This can happen if the connection timed out or there was an authentication issue.
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={goBack}>Return to Admin Panel</Button>
              <Button onClick={retryConnection}>Try Again</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const items = type === "google_ads" ? accounts : properties;
  const title = type === "google_ads" ? "Select Google Ads Account" : "Select GA4 Property";
  const description = type === "google_ads" 
    ? "Choose which Google Ads account to connect to j-MAP"
    : "Choose which Google Analytics 4 property to connect to j-MAP";
  const Icon = type === "google_ads" ? BarChart3 : LineChart;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto p-3 bg-blue-100 rounded-full w-fit mb-4">
            <Icon className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2">Loading accounts...</span>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                No {type === "google_ads" ? "accounts" : "properties"} found.
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Make sure your Google account has access to {type === "google_ads" ? "Google Ads" : "Google Analytics"}.
              </p>
              <Button variant="outline" onClick={goBack}>
                Return to Admin Panel
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="p-4 border rounded-lg hover:border-blue-500 hover:bg-blue-50/50 cursor-pointer transition-all"
                  onClick={() => !selecting && selectAccount(item.id, item.name)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-full">
                        <Building2 className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium">{item.name}</p>
                        {item.account_name && (
                          <p className="text-sm text-muted-foreground">
                            Account: {item.account_name}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          ID: {item.id}
                        </p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      disabled={selecting}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAccount(item.id, item.name);
                      }}
                    >
                      {selecting ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Select
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
              
              <div className="pt-4 border-t">
                <Button variant="outline" className="w-full" onClick={goBack}>
                  Cancel and Return
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GoogleAccountSelectPage;
